import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
  Loader2, GitCompare, ArrowLeft, CheckCircle2, AlertTriangle, Trophy,
  XCircle, ChevronRight,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";

const LEVEL = {
  low: { color: "text-emerald-600", bg: "bg-emerald-50", ring: "ring-emerald-200" },
  medium: { color: "text-amber-600", bg: "bg-amber-50", ring: "ring-amber-200" },
  high: { color: "text-orange-600", bg: "bg-orange-50", ring: "ring-orange-200" },
  critical: { color: "text-rose-600", bg: "bg-rose-50", ring: "ring-rose-200" },
};

export default function Compare() {
  const navigate = useNavigate();
  const [scans, setScans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(new Set());
  const [running, setRunning] = useState(false);
  const [result, setResult] = useState(null);
  const [searchParams] = useSearchParams();

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const r = await api.get("/scans");
        const ready = (r.data || []).filter((s) => s.status === "ready");
        if (!alive) return;
        setScans(ready);
      } catch (err) {
        if (alive) toast.error("Couldn't load scans", { description: formatApiError(err) });
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, []);

  // Pre-select scans whose IDs are in the ?ids=a,b URL param. Runs every
  // time `scans` changes (typically once, after fetch) until we've matched.
  const presetIds = searchParams.get("ids");
  useEffect(() => {
    if (!presetIds || scans.length === 0) return;
    const wanted = presetIds.split(",").map((s) => s.trim()).filter(Boolean);
    const matched = wanted.filter((x) => scans.some((s) => s.id === x));
    if (matched.length > 0) {
      setSelected(new Set(matched.slice(0, 5)));
    }
  }, [presetIds, scans]);

  const toggle = (id) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else if (next.size < 5) {
        next.add(id);
      } else {
        toast.warning("Maximum 5 documents per comparison");
      }
      return next;
    });
  };

  const runCompare = async () => {
    if (selected.size < 2) {
      toast.error("Pick at least 2 scans to compare");
      return;
    }
    setRunning(true);
    setResult(null);
    try {
      const r = await api.post("/scans/compare", {
        scan_ids: Array.from(selected),
      });
      setResult(r.data);
    } catch (err) {
      toast.error("Comparison failed", { description: formatApiError(err) });
    } finally {
      setRunning(false);
    }
  };

  const labels = useMemo(() => result?.labels || [], [result]);

  if (loading) {
    return (
      <div className="flex justify-center py-20">
        <Loader2 className="h-6 w-6 animate-spin text-blue-600" />
      </div>
    );
  }

  return (
    <div className="space-y-8" data-testid="compare-page">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <p className="text-xs uppercase tracking-[0.22em] font-semibold text-blue-600">
            Decision support
          </p>
          <h1 className="mt-1.5 font-display text-3xl font-semibold tracking-tight text-slate-900">
            Contract comparison
          </h1>
          <p className="mt-1.5 text-sm text-slate-600 max-w-2xl">
            Pick 2–5 scans from the list below. AgentFlow will rank them by
            risk, highlight side-by-side differences across the dimensions
            that matter, and tell you which one is safer to sign.
          </p>
        </div>
        {result && (
          <Button variant="outline" onClick={() => setResult(null)} data-testid="compare-new">
            <ArrowLeft className="h-3.5 w-3.5 mr-1.5" /> New comparison
          </Button>
        )}
      </div>

      {!result ? (
        <SelectionStep
          scans={scans}
          selected={selected}
          toggle={toggle}
          running={running}
          onRun={runCompare}
          onNew={() => navigate("/scans")}
        />
      ) : (
        <ResultsView result={result} labels={labels} />
      )}
    </div>
  );
}

function SelectionStep({ scans, selected, toggle, running, onRun, onNew }) {
  return (
    <>
      <div className="rounded-2xl border border-slate-200 bg-white shadow-sm">
        <div className="flex items-center justify-between px-5 py-4 border-b border-slate-100">
          <div>
            <h2 className="font-display text-base font-semibold text-slate-900">
              Select scans to compare
            </h2>
            <p className="text-xs text-slate-500 mt-0.5">
              Showing completed scans only. Selected: <b>{selected.size}</b> / 5
            </p>
          </div>
          <Button
            onClick={onRun}
            disabled={selected.size < 2 || running}
            className="bg-blue-600 hover:bg-blue-700 text-white h-9"
            data-testid="compare-run"
          >
            {running ? (
              <><Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" /> Comparing…</>
            ) : (
              <><GitCompare className="h-3.5 w-3.5 mr-1.5" /> Compare selected</>
            )}
          </Button>
        </div>
        {scans.length === 0 ? (
          <div className="px-5 py-10 text-center">
            <p className="text-sm text-slate-500 mb-3">No completed scans yet.</p>
            <Button variant="outline" onClick={onNew}>Run your first scan</Button>
          </div>
        ) : (
          <div className="divide-y divide-slate-100" data-testid="compare-scan-list">
            {scans.map((s) => {
              const isSel = selected.has(s.id);
              return (
                <label
                  key={s.id}
                  className={`flex items-start gap-3 px-5 py-3 cursor-pointer hover:bg-slate-50 ${isSel ? "bg-blue-50/40" : ""}`}
                  data-testid={`compare-pick-${s.id}`}
                >
                  <Checkbox
                    checked={isSel}
                    onCheckedChange={() => toggle(s.id)}
                    className="mt-1"
                  />
                  <div className="flex-1 min-w-0">
                    <div className="font-medium text-slate-900 truncate">
                      {s.document_name || "Ad-hoc clause"}
                    </div>
                    <div className="text-xs text-slate-500">
                      {new Date(s.created_at).toLocaleString()} ·{" "}
                      Score <b>{s.score}/100</b> ·{" "}
                      <RiskPill level={s.level} />
                    </div>
                  </div>
                </label>
              );
            })}
          </div>
        )}
      </div>
    </>
  );
}

function ResultsView({ result, labels }) {
  return (
    <div className="space-y-6" data-testid="compare-results">
      {/* Verdict banner */}
      <div className="rounded-2xl border border-blue-200 bg-gradient-to-br from-blue-50 to-white p-6 shadow-sm">
        <div className="flex items-center gap-2 text-xs uppercase tracking-[0.18em] font-semibold text-blue-600">
          <Trophy className="h-4 w-4" /> Verdict
        </div>
        <p className="mt-2 font-display text-2xl font-semibold tracking-tight text-slate-900" data-testid="compare-verdict">
          {result.verdict}
        </p>
        {result.summary && (
          <p className="mt-3 text-sm text-slate-600 max-w-3xl">{result.summary}</p>
        )}
      </div>

      {/* Side-by-side header — quick visual hierarchy */}
      <SideBySideHeader result={result} />

      {/* Rankings grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {result.rankings.map((r) => {
          const isSafer = r.index === result.safer_index;
          const isRiskier = r.index === result.riskier_index;
          const level = LEVEL[r.level] || LEVEL.medium;
          return (
            <div key={r.index}
                 className={`rounded-2xl border bg-white p-5 shadow-sm relative ${
                   isSafer ? "border-emerald-300" : isRiskier ? "border-rose-300" : "border-slate-200"
                 }`}
                 data-testid={`rank-card-${r.index}`}>
              {isSafer && (
                <span className="absolute -top-2 -right-2 inline-flex items-center gap-1 rounded-full bg-emerald-600 text-white text-[10px] uppercase tracking-wider font-semibold px-2 py-1">
                  <CheckCircle2 className="h-3 w-3" /> Safer
                </span>
              )}
              {isRiskier && (
                <span className="absolute -top-2 -right-2 inline-flex items-center gap-1 rounded-full bg-rose-600 text-white text-[10px] uppercase tracking-wider font-semibold px-2 py-1">
                  <XCircle className="h-3 w-3" /> Riskier
                </span>
              )}
              <div className="text-xs uppercase tracking-[0.18em] font-semibold text-slate-500">
                {r.label || `Document ${r.index + 1}`}
              </div>
              <div className="mt-1 text-[10px] text-slate-400 font-mono">
                position #{r.index + 1}
              </div>
              <div className={`mt-3 font-display text-4xl font-semibold tabular-nums ${level.color}`}>
                {r.score}<span className="text-base text-slate-400">/100</span>
              </div>
              <span className={`mt-2 inline-flex items-center rounded-full px-2 py-0.5 text-[10px] uppercase tracking-wider font-semibold ring-1 ring-inset ${level.bg} ${level.color} ${level.ring}`}>
                {r.level} risk
              </span>
            </div>
          );
        })}
      </div>

      {/* By-topic table */}
      {result.by_topic?.length > 0 && (
        <div className="rounded-2xl border border-slate-200 bg-white shadow-sm">
          <div className="px-5 py-4 border-b border-slate-100">
            <h2 className="font-display text-base font-semibold text-slate-900">
              Topic-by-topic breakdown
            </h2>
          </div>
          <table className="min-w-full text-sm" data-testid="compare-topic-table">
            <thead className="bg-slate-50 text-left text-xs uppercase tracking-[0.12em] text-slate-500 font-semibold">
              <tr>
                <th className="px-5 py-3">Topic</th>
                <th className="px-5 py-3">Winner</th>
                <th className="px-5 py-3">Notes</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {result.by_topic.map((t, i) => (
                <tr key={i} className="hover:bg-slate-50">
                  <td className="px-5 py-3 font-medium text-slate-900">{t.topic}</td>
                  <td className="px-5 py-3">
                    <span className="inline-flex items-center gap-1.5 rounded-full bg-emerald-50 text-emerald-700 ring-1 ring-inset ring-emerald-200 px-2 py-0.5 text-xs">
                      <CheckCircle2 className="h-3 w-3" /> {labels[t.winner_index] || `Position #${t.winner_index + 1}`}
                    </span>
                  </td>
                  <td className="px-5 py-3 text-slate-700">{t.notes}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Key differences */}
      {result.key_differences?.length > 0 && (
        <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
          <h2 className="font-display text-base font-semibold text-slate-900 mb-3">
            Key differences
          </h2>
          <ul className="space-y-2.5" data-testid="compare-diffs">
            {result.key_differences.map((d, i) => (
              <li key={i} className="flex items-start gap-2 text-sm text-slate-700">
                <ChevronRight className="h-4 w-4 mt-0.5 text-blue-600 shrink-0" />
                <span>{d}</span>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}

function SideBySideHeader({ result }) {
  // Only render when comparing exactly 2 documents — side-by-side is most
  // informative as a head-to-head. For 3+, the ranking cards do the job.
  if (!result?.rankings || result.rankings.length !== 2) return null;
  const safer = result.rankings.find((r) => r.index === result.safer_index)
              || result.rankings[0];
  const riskier = result.rankings.find((r) => r.index === result.riskier_index)
               || result.rankings[1];
  if (safer.index === riskier.index) return null;
  const saferL = LEVEL[safer.level] || LEVEL.medium;
  const riskierL = LEVEL[riskier.level] || LEVEL.medium;
  const delta = Math.abs((riskier.score || 0) - (safer.score || 0));

  return (
    <div className="rounded-2xl border border-slate-200 bg-white shadow-sm overflow-hidden" data-testid="side-by-side-header">
      <div className="grid grid-cols-2 divide-x divide-slate-200">
        {/* Safer */}
        <div className="p-5 sm:p-6 bg-emerald-50/30 relative">
          <span className="inline-flex items-center gap-1 rounded-full bg-emerald-600 text-white text-[10px] uppercase tracking-wider font-semibold px-2 py-1">
            <Trophy className="h-3 w-3" /> Safer choice
          </span>
          <h3 className="mt-3 font-display text-lg font-semibold text-slate-900 line-clamp-2">
            {safer.label}
          </h3>
          <div className="mt-3 flex items-baseline gap-3">
            <div className={`font-display text-4xl font-semibold tabular-nums ${saferL.color}`}>
              {safer.score}
              <span className="text-base text-slate-400 font-normal">/100</span>
            </div>
            <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-[10px] uppercase tracking-wider font-semibold ring-1 ring-inset ${saferL.bg} ${saferL.color} ${saferL.ring}`}>
              {safer.level} risk
            </span>
          </div>
        </div>

        {/* Riskier */}
        <div className="p-5 sm:p-6 bg-rose-50/30 relative">
          <span className="inline-flex items-center gap-1 rounded-full bg-rose-600 text-white text-[10px] uppercase tracking-wider font-semibold px-2 py-1">
            <XCircle className="h-3 w-3" /> Riskier draft
          </span>
          <h3 className="mt-3 font-display text-lg font-semibold text-slate-900 line-clamp-2">
            {riskier.label}
          </h3>
          <div className="mt-3 flex items-baseline gap-3">
            <div className={`font-display text-4xl font-semibold tabular-nums ${riskierL.color}`}>
              {riskier.score}
              <span className="text-base text-slate-400 font-normal">/100</span>
            </div>
            <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-[10px] uppercase tracking-wider font-semibold ring-1 ring-inset ${riskierL.bg} ${riskierL.color} ${riskierL.ring}`}>
              {riskier.level} risk
            </span>
          </div>
        </div>
      </div>
      {/* Delta strip */}
      <div className="px-5 py-2.5 bg-slate-50 border-t border-slate-200 flex items-center justify-center gap-2 text-xs text-slate-600">
        <span className="font-semibold tabular-nums text-slate-900">{delta}</span>
        <span>point risk-score gap between the two drafts</span>
      </div>
    </div>
  );
}


function RiskPill({ level }) {
  const cfg = LEVEL[level] || LEVEL.medium;
  return (
    <span className={`inline-flex items-center rounded-full px-1.5 py-0.5 text-[10px] uppercase tracking-wider font-semibold ring-1 ring-inset ${cfg.bg} ${cfg.color} ${cfg.ring}`}>
      {level}
    </span>
  );
}
